import { useMemo, useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { Botao, Selo } from '../../componentes/base';
import { Vazio } from '../../componentes/estados';
import { AreaSensivel, Rotulo, Selecao } from '../../componentes/formulario';
import { Drawer, Modal } from '../../componentes/sobreposicao';
import { Tabela, type Coluna } from '../../componentes/tabela';
import { useToast } from '../../componentes/toast';
import { mensagemDeErro, post } from '../../nucleo/api';
import { data, dataHora, nomePessoa, numero } from '../../nucleo/formato';
import { useSessao } from '../../nucleo/sessao';
import { COR_ORIGEM, ORIGENS, invalidarPonto, type Marcacao, type Origem } from './tipos';

/** Marcações (as próprias; com ver todos, as da equipe), com a origem, fora de linha, cerca e o pedido de desconsiderar. */
export function Marcacoes() {
  const { t } = useTranslation('ponto');
  const [origem, setOrigem] = useState<Origem | ''>('');
  const [aberta, setAberta] = useState<Marcacao | null>(null);
  const colunas: Coluna<Marcacao>[] = [
    { chave: 'funcionario_nome', rotulo: t('campos.funcionario'), principal: true, render: (m) => <span className="font-medium">{nomePessoa(m.funcionario_nome) || '-'}</span> },
    { chave: 'data_referencia', rotulo: t('campos.dia'), render: (m) => data(m.data_referencia) },
    { chave: 'hora', rotulo: t('campos.hora'), mono: true },
    { chave: 'origem', rotulo: t('campos.origem'), render: (m) => <Selo cor={COR_ORIGEM[m.origem]}>{t(`origem.${m.origem}`)}</Selo> },
    {
      chave: 'situacao',
      rotulo: t('campos.situacao'),
      render: (m) => (
        <span className="flex flex-wrap gap-1">
          {m.desconsiderada && <Selo cor="neutro">{t('desconsiderada')}</Selo>}
          {m.ajuste_pendente && <Selo cor="laranja">{t('ajustePendente')}</Selo>}
          {m.fora_de_linha && <Selo cor="laranja">{t('campos.foraDeLinha')}</Selo>}
          {m.dentro_da_cerca === false && <Selo cor="perigo">{t('fora')}</Selo>}
        </span>
      ),
    },
  ];
  const params = useMemo(() => (origem ? { origem } : {}), [origem]);
  return (
    <>
      <Tabela<Marcacao>
        tela="ponto.marcacoes"
        caminho="/ponto/marcacoes"
        params={params}
        chaveConsulta={['ponto', 'marcacoes', origem]}
        colunas={colunas}
        aoClicar={(m) => setAberta(m)}
        buscaNaUrl
        filtros={{ origem }}
        aoAplicarFiltros={(f) => setOrigem(ORIGENS.includes(f.origem as Origem) ? (f.origem as Origem) : '')}
        filtrosExtra={
          <Selecao
            aria-label={t('campos.origem')}
            value={origem}
            onChange={(e) => setOrigem(e.target.value as Origem | '')}
            opcoes={[{ valor: '', rotulo: t('estado.todos') }, ...ORIGENS.map((o) => ({ valor: o, rotulo: t(`origem.${o}`) }))]}
          />
        }
        vazio={<Vazio texto={t('vazio.marcacoes')} />}
      />
      {aberta && <FichaMarcacao marcacao={aberta} aoFechar={() => setAberta(null)} />}
    </>
  );
}

function FichaMarcacao({ marcacao: m, aoFechar }: { marcacao: Marcacao; aoFechar: () => void }) {
  const { t } = useTranslation('ponto');
  const { t: tn } = useTranslation();
  const s = useSessao();
  const qc = useQueryClient();
  const toast = useToast();
  const [pedindo, setPedindo] = useState(false);
  const [justificativa, setJustificativa] = useState('');
  const pedir = useMutation({
    mutationFn: () => post('/ponto/ajustes', { id: crypto.randomUUID(), funcionario_id: m.funcionario_id, tipo: 'desconsiderar', marcacao_id: m.id, justificativa }),
    onSuccess: async () => {
      await invalidarPonto(qc);
      toast.sucesso(tn('comum.salvo'));
      aoFechar();
    },
    onError: (e) => toast.erro(mensagemDeErro(e)),
  });
  const linha = (rotulo: string, valor: React.ReactNode) => (
    <div className="flex flex-wrap justify-between gap-2 border-b border-borda py-2 text-sm last:border-0">
      <dt className="text-tinta-3">{rotulo}</dt>
      <dd className="min-w-0 text-right text-tinta-1">{valor}</dd>
    </div>
  );
  const podePedir = s.pode('ponto.criar') && !m.desconsiderada && !m.ajuste_pendente;
  return (
    <>
      <Drawer
        aberto={!pedindo}
        aoFechar={aoFechar}
        titulo={`${nomePessoa(m.funcionario_nome)} · ${data(m.data_referencia)} ${m.hora}`}
        rodape={
          podePedir ? (
            <Botao onClick={() => setPedindo(true)}>{t('acoes.pedirDesconsiderar')}</Botao>
          ) : undefined
        }
      >
        <dl>
          {linha(t('campos.origem'), <Selo cor={COR_ORIGEM[m.origem]}>{t(`origem.${m.origem}`)}</Selo>)}
          {linha(t('campos.recebidaEm'), dataHora(m.recebida_em))}
          {m.hora_aparelho && linha(t('campos.horaAparelho'), dataHora(m.hora_aparelho))}
          {linha(t('campos.foraDeLinha'), m.fora_de_linha ? t('sim') : t('nao'))}
          {m.latitude !== null && m.longitude !== null && linha(t('campos.localizacao'), `${numero(m.latitude, 5)}, ${numero(m.longitude, 5)}${m.precisao_m !== null ? ` (${numero(m.precisao_m)} m)` : ''}`)}
          {m.dentro_da_cerca !== null && linha(t('campos.cerca'), m.dentro_da_cerca ? t('dentro') : t('fora'))}
          {m.foto_anexo_id && linha(t('campos.foto'), <a className="text-acento-forte hover:underline" download href={`/api/v1/anexos/${m.foto_anexo_id}`}>{t('acoes.baixarFoto')}</a>)}
          {m.ip && linha(t('campos.ip'), m.ip)}
          {linha(t('fiscal.comprovante'), <a className="text-acento-forte hover:underline" download href={`/api/v1/ponto/marcacoes/${m.id}/comprovante`}>{t('fiscal.baixarComprovante')}</a>)}
          {m.desconsiderada && linha(t('campos.situacao'), <Selo cor="neutro">{t('desconsiderada')}</Selo>)}
          {m.ajuste_pendente && linha(t('campos.situacao'), <Selo cor="laranja">{t('ajustePendente')}</Selo>)}
        </dl>
      </Drawer>
      <Modal
        aberto={pedindo}
        aoFechar={() => setPedindo(false)}
        titulo={t('acoes.pedirDesconsiderar')}
        rodape={
          <>
            <Botao variante="fantasma" onClick={() => setPedindo(false)}>
              {tn('comum.cancelar')}
            </Botao>
            <Botao variante="primario" carregando={pedir.isPending} aria-disabled={!justificativa.trim()} onClick={() => justificativa.trim() && pedir.mutate()}>
              {tn('comum.salvar')}
            </Botao>
          </>
        }
      >
        <Rotulo id="ponto-justificativa" rotulo={t('campos.justificativa')} obrigatorio ajuda={t('campos.justificativaAjuda')}>
          <AreaSensivel
            id="ponto-justificativa"
            rows={3}
            maxLength={500}
            value={justificativa}
            onChange={(e) => setJustificativa(e.target.value)}
            className="w-full min-w-0 rounded-lg border border-borda-campo bg-campo px-3 py-2 text-sm text-tinta-1 focus:border-foco focus:outline-2 focus:outline-offset-0 focus:outline-foco"
          />
        </Rotulo>
      </Modal>
    </>
  );
}
